import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  useRecordContext,
  useNotify,
  useRefresh,
  useDataProvider,
  Button,
  useTranslate,
  DeleteButton,
  EditButton,
  FunctionField,
} from 'react-admin';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Chip,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  useMediaQuery,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn,
  ElectricBolt,
  Equalizer,
  Settings,
} from '@mui/icons-material';

interface AssignmentsTabProps {
  groupId: string;
}

const notificationTypes = [
  { id: 'payment_success', name: 'Payment Success' },
  { id: 'payment_failed', name: 'Payment Failed' },
  { id: 'charging_started', name: 'Charging Started' },
  { id: 'charging_completed', name: 'Charging Completed' },
  { id: 'charger_offline', name: 'Charger Offline' },
  { id: 'charger_error', name: 'Charger Error' },
  { id: 'maintenance_required', name: 'Maintenance Required' },
  { id: 'load_management_alert', name: 'Load Management Alert' },
  { id: 'system_alert', name: 'System Alert' },
];

const getTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    site: '#1976D2',
    charger: '#FF9800',
    load_group: '#9C27B0',
    system: '#607D8B',
  };
  return colors[type] || '#757575';
};

const ResourceTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'site':
      return <LocationOn fontSize="small" />;
    case 'charger':
      return <ElectricBolt fontSize="small" />;
    case 'load_group':
      return <Equalizer fontSize="small" />;
    case 'system':
      return <Settings fontSize="small" />;
    default:
      return null;
  }
};

const wrappedChipSx = {
  maxWidth: '100%',
  minWidth: 0,
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
    py: 0.5,
  },
};

const NotificationTypesField = () => {
  const record = useRecordContext();
  if (!record || !record.notificationTypes) return null;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {record.notificationTypes.map((type: string) => (
        <Chip
          key={type}
          label={notificationTypes.find(nt => nt.id === type)?.name || type}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        />
      ))}
    </Box>
  );
};

interface MobileAssignmentsListProps {
  assignments: any[];
  onEdit: (assignment: any) => void;
  onDelete: (assignmentId: string, groupId: string) => void;
  groupId: string;
}

const MobileAssignmentsList: React.FC<MobileAssignmentsListProps> = ({
  assignments,
  onEdit,
  onDelete,
  groupId,
}) => {
  return (
    <Box>
      {assignments.map((assignment: any) => (
        <Box
          key={assignment.id}
          sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                icon={<ResourceTypeIcon type={assignment.resourceType} />}
                label={assignment.resourceType.replace('_', ' ')}
                size="small"
                sx={{
                  ...wrappedChipSx,
                  height: 'auto',
                  textTransform: 'capitalize',
                  borderColor: getTypeColor(assignment.resourceType),
                  color: getTypeColor(assignment.resourceType),
                  '& .MuiChip-icon': {
                    color: getTypeColor(assignment.resourceType),
                  },
                }}
                variant="outlined"
              />
            </Box>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: assignment.active ? '#4CAF50' : '#F44336',
                flexShrink: 0,
              }}
            />
          </Box>

          <Typography
            variant="body2"
            sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.5 }}
          >
            {assignment.resourceName || 'System'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
            {assignment.notificationTypes?.map((type: string) => (
              <Chip
                key={type}
                label={notificationTypes.find(nt => nt.id === type)?.name || type}
                size="small"
                sx={{ ...wrappedChipSx, height: 'auto', fontSize: '0.75rem' }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString() : ''}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => onEdit(assignment)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(String(assignment.id), groupId)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const AssignmentsTab = ({ groupId }: AssignmentsTabProps) => {
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<any>(null);
  const [formData, setFormData] = useState<{
    resourceType: string;
    resourceId: string;
    notificationTypes: string[];
  }>({
    resourceType: 'site',
    resourceId: '',
    notificationTypes: [],
  });
  const [resources, setResources] = useState<any[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);

  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    fetchAssignments();
  }, [groupId]);

  React.useEffect(() => {
    if (formData.resourceType && formData.resourceType !== 'system') {
      fetchResources(formData.resourceType);
    }
  }, [formData.resourceType]);

  const fetchResources = async (resourceType: string) => {
    setLoadingResources(true);
    try {
      let resource = resourceType;
      if (resourceType === 'load_group') {
        resource = 'load-groups';
      } else if (resourceType === 'site') {
        resource = 'sites';
      } else if (resourceType === 'charger') {
        resource = 'chargers';
      }

      const { data } = await dataProvider.getList(resource, {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });

      setResources(data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      notify('Failed to load resources', { type: 'error' });
    } finally {
      setLoadingResources(false);
    }
  };

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await dataProvider.getList('notification-groups-assignments', {
        filter: { groupId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'assignedAt', order: 'DESC' },
      });
      setAssignments(response.data);
    } catch (error) {
      notify('Failed to load assignments', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAssignment(null);
    setFormData({
      resourceType: 'site',
      resourceId: '',
      notificationTypes: [],
    });
    setOpenDialog(true);
  };

  const handleEdit = (assignment: any) => {
    setEditingAssignment(assignment);
    setFormData({
      resourceType: assignment.resourceType,
      resourceId: assignment.resourceId,
      notificationTypes: assignment.notificationTypes,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (assignmentId: string, groupId: string) => {
    try {
      await dataProvider.delete('notification-groups-assignments', {
        id: assignmentId,
        previousData: { id: assignmentId, groupId },
      });
      notify('Assignment deleted successfully');
      fetchAssignments();
    } catch (error) {
      notify('Failed to delete assignment', { type: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      if (editingAssignment) {
        await dataProvider.update('notification-groups-assignments', {
          id: editingAssignment.id,
          data: {
            notificationTypes: formData.notificationTypes,
          },
          previousData: editingAssignment,
        });
        notify('Assignment updated successfully');
      } else {
        const assignmentData: any = {
          groupId,
          resourceType: formData.resourceType,
          notificationTypes: formData.notificationTypes,
        };

        if (formData.resourceType !== 'system') {
          assignmentData.resourceId = formData.resourceId;
        }

        await dataProvider.create('notification-groups-assignments', {
          data: assignmentData,
        });
        notify('Assignment created successfully');
      }
      setOpenDialog(false);
      fetchAssignments();
    } catch (error: any) {
      notify(error.message || 'Failed to save assignment', { type: 'error' });
    }
  };


  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">{translate('resources.notification-groups.assignments.title')}</Typography>
        <Button
          label={translate('resources.notification-groups.assignments.addButton')}
          onClick={handleAdd}
          startIcon={<AddIcon />}
        />
      </Box>

      {loading ? (
        <Typography>{translate('resources.notification-groups.assignments.loading')}</Typography>
      ) : assignments.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body1" color="textSecondary">
            {translate('resources.notification-groups.assignments.noAssignments')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {translate('resources.notification-groups.assignments.noAssignmentsHelp')}
          </Typography>
        </Box>
      ) : isMobile ? (
        <MobileAssignmentsList
          assignments={assignments}
          onEdit={handleEdit}
          onDelete={handleDelete}
          groupId={groupId}
        />
      ) : (
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          <List>
            <Datagrid data={assignments} bulkActionButtons={false}>
              <FunctionField
                label="Type"
                render={(record: any) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ResourceTypeIcon type={record.resourceType} />
                    <span style={{ textTransform: 'capitalize' }}>
                      {record.resourceType.replace('_', ' ')}
                    </span>
                  </Box>
                )}
              />
              <TextField source="resourceName" label="Resource" />
              <NotificationTypesField />
              <BooleanField source="active" label="Active" />
              <DateField source="assignedAt" label="Assigned" showTime />
              <FunctionField
                render={(record: any) => (
                  <Box sx={{ textAlign: 'right' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(record)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(String(record.id), groupId)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              />
            </Datagrid>
          </List>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAssignment ? 'Edit Assignment' : 'Add Resource Assignment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Resource Type</InputLabel>
                <Select
                  value={formData.resourceType}
                  onChange={(e) => setFormData({ ...formData, resourceType: e.target.value, resourceId: '' })}
                  disabled={!!editingAssignment}
                  label="Resource Type"
                >
                  <MenuItem value="site">Site</MenuItem>
                  <MenuItem value="charger">Charger</MenuItem>
                  <MenuItem value="load_group">Load Group</MenuItem>
                  <MenuItem value="system">System</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {formData.resourceType !== 'system' && (
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Resource</InputLabel>
                  <Select
                    value={formData.resourceId}
                    onChange={(e) => setFormData({ ...formData, resourceId: e.target.value })}
                    disabled={!!editingAssignment || loadingResources}
                    label="Select Resource"
                  >
                    {loadingResources ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : resources.length === 0 ? (
                      <MenuItem disabled>No resources available</MenuItem>
                    ) : (
                      resources.map((resource) => (
                        <MenuItem key={resource.id} value={resource.id}>
                          {resource.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box>
              <FormControl fullWidth>
                <InputLabel>Notification Types</InputLabel>
                <Select
                  multiple
                  value={formData.notificationTypes}
                  onChange={(e) => setFormData({ ...formData, notificationTypes: e.target.value as string[] })}
                  label="Notification Types"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip
                          key={value}
                          label={notificationTypes.find(nt => nt.id === value)?.name || value}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {notificationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Checkbox checked={formData.notificationTypes.indexOf(type.id) > -1} />
                      <ListItemText primary={type.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                Select one or more notification types
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpenDialog(false)}>
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleSave}
            variant="contained"
            disabled={!formData.notificationTypes.length || (!editingAssignment && formData.resourceType !== 'system' && !formData.resourceId)}
          >
            {editingAssignment ? 'Update' : 'Add'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};